import { useEffect, useRef, useState, type ReactNode } from "react";

import { Feder, Geschwindigkeitsmesser, gummiband, projiziere, reduzierteBewegung } from "../../lib/spring";

/**
 * Blatt von unten. Es kommt und geht auf demselben Weg, lässt sich jederzeit
 * greifen – auch mitten in der Bewegung – und folgt dann dem Finger 1:1.
 * Beim Loslassen entscheidet die projizierte Ruhelage, nicht die Position.
 */
export function Blatt({ offen, onClose, titel, children }: { offen: boolean; onClose: () => void; titel: string; children: ReactNode }) {
  const [montiert, setMontiert] = useState(offen);
  const blatt = useRef<HTMLDivElement>(null);
  const schleier = useRef<HTMLDivElement>(null);
  const feder = useRef<Feder | null>(null);
  const messer = useRef(new Geschwindigkeitsmesser());
  const griff = useRef<{ zeigerY: number; y: number } | null>(null);
  const inhalt = useRef<HTMLDivElement>(null);
  const hoehe = () => blatt.current?.offsetHeight ?? window.innerHeight;

  /** Zeigt an, ob unterhalb noch Inhalt wartet – nach Größenänderung und beim Scrollen neu. */
  const pruefeWeiter = () => {
    const el = inhalt.current;
    if (!el) return;
    const weiter = el.scrollHeight - el.clientHeight - el.scrollTop > 4;
    if (weiter) el.setAttribute("data-weiter", "");
    else el.removeAttribute("data-weiter");
  };
  useEffect(() => {
    if (!montiert) return;
    pruefeWeiter();
    const ro = new ResizeObserver(pruefeWeiter);
    if (inhalt.current) ro.observe(inhalt.current);
    return () => ro.disconnect();
  }, [montiert, children]);

  const male = (y: number) => {
    if (blatt.current) blatt.current.style.transform = `translate3d(0, ${y}px, 0)`;
    if (schleier.current) schleier.current.style.opacity = String(Math.max(0, 1 - y / hoehe()));
  };
  if (!feder.current) feder.current = new Feder(0, male, { response: 0.32, damping: 0.85 });

  useEffect(() => {
    if (offen) setMontiert(true);
  }, [offen]);

  useEffect(() => {
    if (!montiert) return;
    const f = feder.current!;
    if (offen) {
      // Vom aktuellen Wert aus – beim ersten Öffnen von ganz unten.
      if (f.wert === 0 && f.ziel === 0) f.setze(hoehe());
      f.zu(0);
    } else {
      f.zu(hoehe(), { onSettle: () => setMontiert(false) });
    }
  }, [offen, montiert]);

  useEffect(() => {
    if (!offen) return;
    const taste = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", taste);
    return () => window.removeEventListener("keydown", taste);
  }, [offen, onClose]);

  if (!montiert) return null;

  const f = feder.current!;
  return (
    <div className="kalk-blatt-ebene">
      <div ref={schleier} className="kalk-schleier" onClick={onClose} />
      <div
        ref={blatt}
        className="kalk-blatt"
        role="dialog"
        aria-modal="true"
        aria-label={titel}
        style={{ transform: `translate3d(0, ${f.wert}px, 0)` }}
      >
        <div
          className="kalk-blatt__griff"
          onPointerDown={(e) => {
            f.stop();
            griff.current = { zeigerY: e.clientY, y: f.wert };
            messer.current.reset();
            e.currentTarget.setPointerCapture(e.pointerId);
          }}
          onPointerMove={(e) => {
            if (!griff.current) return;
            const roh = griff.current.y + (e.clientY - griff.current.zeigerY);
            const y = roh < 0 ? gummiband(roh, hoehe()) : roh;
            messer.current.erfasse(y);
            f.setze(y);
          }}
          onPointerUp={() => {
            if (!griff.current) return;
            griff.current = null;
            const v = messer.current.wert();
            const ruhe = f.wert + projiziere(v);
            if (ruhe > hoehe() * 0.4 || v > 1200) {
              f.zu(hoehe(), { geschwindigkeit: v, onSettle: () => setMontiert(false) });
              onClose();
            } else {
              f.zu(0, { geschwindigkeit: v });
            }
          }}
          onPointerCancel={() => { griff.current = null; f.zu(0); }}
        >
          <span className="kalk-blatt__balken" aria-hidden="true" />
          <strong>{titel}</strong>
          <button type="button" className="kalk-symbolknopf" aria-label="Schließen" onClick={onClose}>×</button>
        </div>
        <div ref={inhalt} className="kalk-blatt__inhalt" data-reduziert={reduzierteBewegung() || undefined} onScroll={pruefeWeiter}>{children}</div>
      </div>
    </div>
  );
}
